import type { TableRow } from "@/components/ui/standardized-table";
import type {
  PagesLandingPageItem,
  PagesLandingPageTotals,
} from "@/backend-sdk/client/types.gen";
import { rodapeDoConjunto } from "@/lib/rodape-do-conjunto";

/**
 * <M-234> A tradução de uma linha de `GET /api/v1/pages/landing-page` para as chaves que a
 * tabela PÁGINA DE ENTRADA da aba do SEO desenha — e o rodapé pela mesma função.
 *
 * A rota publica `sessoes_percent_change`; a tabela da aba batiza a coluna `sessoesChange`.
 * Enquanto a página só desenhava linhas, a tradução podia morar embutida no `useMemo`; a
 * partir do momento em que o "Total Geral" passa a vir do servidor, ela precisa ser **a
 * mesma** para a linha e para o rodapé. Traduzir o rodapé por outro caminho o deixaria vazio
 * na tela sem que nada falhasse — o defeito que M-225 encontrou nas três tabelas da Análise
 * de Ticket Médio, e que M-230 e M-232 evitaram por este mesmo meio.
 *
 * Ela mora aqui, e não no módulo da página, porque uma regra dentro do módulo da página não
 * tem como ser testada sem desenhar a página inteira, e exportá-la de lá acenderia
 * `react-refresh/only-export-components`.
 *
 * ⚠️ A página `/dashboard/pages` consome a **mesma** rota e não passa por aqui: as colunas de
 * lá desenham as chaves do payload direto, sem rebatismo. Quem precisa da tradução é a aba do
 * SEO, e é só dela que esta função fala.
 */

/** O rótulo cru de `sessionDefaultChannelGroup` que o produto chama de orgânico. */
export const CANAL_ORGANICO = "Organic Search";

/**
 * O número, ou `null` — que `renderNumericValue` e `renderDeltaCell` desenham como `N/D`.
 *
 * O SDK declara os campos como opcionais e anuláveis, e as duas colunas de cada métrica
 * (valor e variação) tratam o ausente da mesma forma. Normalizar aqui é o que permite ao tipo
 * da linha ser honesto sobre o que a tabela recebe.
 */
const numeroOuNulo = (valor: unknown): number | null =>
  typeof valor === "number" && !Number.isNaN(valor) ? valor : null;

export interface LandingPageTableRow extends TableRow {
  paginaDestino: string;
  sessoes: number | null;
  sessoesChange: number | null;
  pedidos: number | null;
  pedidosChange: number | null;
  valorSessao: number | null;
  valorSessaoChange: number | null;
  txConversao: number | null;
  txConversaoChange: number | null;
  receitaTotal: number | null;
  receitaTotalChange: number | null;
}

export const linhaDePaginaDeEntrada = (
  row: PagesLandingPageItem | PagesLandingPageTotals,
): LandingPageTableRow => ({
  paginaDestino: row.paginaDestino ?? "",
  sessoes: numeroOuNulo(row.sessoes),
  sessoesChange: numeroOuNulo(row.sessoes_percent_change),
  pedidos: numeroOuNulo(row.pedidos),
  pedidosChange: numeroOuNulo(row.pedidos_percent_change),
  valorSessao: numeroOuNulo(row.valorSessao),
  valorSessaoChange: numeroOuNulo(row.valorSessao_percent_change),
  txConversao: numeroOuNulo(row.txConversao),
  txConversaoChange: numeroOuNulo(row.txConversao_percent_change),
  receitaTotal: numeroOuNulo(row.receitaTotal),
  receitaTotalChange: numeroOuNulo(row.receitaTotal_percent_change),
});

/**
 * O rodapé da tabela — o `totals` da rota, traduzido, ou `undefined` quando não há conjunto.
 *
 * O `totals` desta rota é obrigatório e sai **zerado** sobre um conjunto vazio, como os das
 * rotas herdadas do contrato congelado; quem decide se ele vira rodapé é `rodapeDoConjunto`,
 * pelo tamanho do conjunto e não pelo da página.
 *
 * ⚠️ Ele **não** entra em `data`. Foi por entrar que o item 20 da auditoria aconteceu: o
 * `StandardizedTable` soma toda coluna numérica das linhas que recebe para montar o rodapé
 * automático, e o rodapé do conjunto virava parcela da própria soma — 25.175 sessões viravam
 * 46.289 na conta auditada.
 */
export const rodapeDePaginasDeEntrada = (
  totals: PagesLandingPageTotals | null,
  totalDoConjunto: number | undefined,
): LandingPageTableRow | undefined => {
  const doConjunto = rodapeDoConjunto(totals, totalDoConjunto);
  return doConjunto === null
    ? undefined
    : linhaDePaginaDeEntrada(doConjunto as unknown as PagesLandingPageTotals);
};
